import { useMemo } from "react"

import { useRouteOrganizationMembers } from "@/hooks/useOrganizationMembers"

export interface ReviewerIdentity {
  email: string
  /** Full name when the member has one set, otherwise the email. */
  name: string
  imageUrl?: string
}

export type ResolveReviewer = (email: string) => ReviewerIdentity

/**
 * Looks a reviewer email up in the route organization's roster, ignoring case
 * and surrounding whitespace. An email with
 * no matching member — a removed user, or the roster still loading — resolves
 * to itself, so callers always have something to render.
 */
export function useResolveReviewer(): ResolveReviewer {
  const { members } = useRouteOrganizationMembers()
  return useMemo<ResolveReviewer>(() => {
    const byEmail = new Map(
      members.map((member) => [member.email.toLowerCase(), member]),
    )
    return (rawEmail) => {
      const email = rawEmail.trim()
      const member = byEmail.get(email.toLowerCase())
      return member
        ? { email, name: member.name, imageUrl: member.imageUrl }
        : { email, name: email }
    }
  }, [members])
}
